import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';

import { exportRegister } from './register-export.js';
import {
  ApiError,
  createSubmittal,
  deleteSubmittal,
  listDeliverables,
  listMaterials,
  listSubmittals,
  moveSubmittal,
  updateSubmittal,
  type Deliverable,
  type Material,
  type SlaStatus,
  type Submittal,
  type SubmittalStatus,
} from '../api.js';
import { formatDate } from '../labels.js';
import { AppLink } from '../link.js';
import { navigate } from '../router.js';
import { Attachments } from './Attachments.js';
import { CrossLink, focusClass, useFocusRow } from './cross-links.js';
import { recordHref, sectionHref } from './project-sections.js';
import { RecordBack, RecordMissing, RecordPage } from './record-page.js';
import {
  Drawer,
  RegisterToolbar,
  RowActions,
  matches,
  useRegisterView,
  type RowAction,
} from './register-kit.js';
import { LOAD_ALL_LIMIT } from '../api/paging.js';

/**
 * Submittals — what has gone out for approval, and how long it has been there
 * (DS-03 / DS-04 / DS-06).
 *
 * `03` marks the approvals inbox as "the *only* screen most consultants ever
 * see", so the age column is the point of the register rather than a detail of
 * it: every row says in words how it stands against its deadline, and the
 * overdue ones say how many days.
 *
 * The decision buttons appear only when the server says this caller may decide.
 * That is not politeness — a Design Coordinator approving their own submittal
 * is the failure the whole module exists to prevent, and the server refuses it
 * too.
 *
 * ON THE REGISTER PATTERN — and the one place it is deliberately bent
 * ------------------------------------------------------------------
 * Every other register puts the next step of the record's life on the row. Here
 * it does that only while the submittal is being MOVED — sent for internal
 * review, sent to the consultant, revised. **When the only moves left are
 * decisions, the row's button is "Open" and every decision sits in the menu.**
 *
 * Putting "Approve" one click from a consultant's finger makes the register
 * argue for one answer, and the answer is the entire product of this module.
 * The decision form is the right amount of friction, and it is also where the
 * words that go on the permanent approval record are typed.
 */

const STATUS_LABELS: Readonly<Record<SubmittalStatus, string>> = {
  draft: 'Draft',
  internal_review: 'Internal review',
  submitted: 'With the consultant',
  approved: 'Approved',
  approved_with_comments: 'Approved with comments',
  rejected: 'Rejected',
  resubmit_required: 'Resubmission required',
  void: 'Void',
};

const STATUS_SYMBOLS: Readonly<Record<SubmittalStatus, string>> = {
  draft: '○',
  internal_review: '◐',
  submitted: '→',
  approved: '✓',
  approved_with_comments: '✓',
  rejected: '✗',
  resubmit_required: '↩',
  void: '×',
};

const SLA_WORDS: Readonly<Record<SlaStatus, string>> = {
  not_sent: 'Not sent yet',
  in_time: 'In time',
  due_soon: 'Due soon',
  overdue: 'Overdue',
  red: 'Badly overdue',
  closed: 'Answered',
};

/** What each button says, in the language somebody would actually use. */
const MOVE_LABELS: Partial<Record<SubmittalStatus, string>> = {
  internal_review: 'Send for internal review',
  submitted: 'Send to the consultant',
  approved: 'Approve',
  approved_with_comments: 'Approve with comments',
  rejected: 'Reject',
  resubmit_required: 'Ask for resubmission',
  draft: 'Revise',
  void: 'Void',
};

/** Moves that record the consultant's words on the permanent approval record. */
const DECISIONS: readonly SubmittalStatus[] = [
  'approved',
  'approved_with_comments',
  'rejected',
  'resubmit_required',
];

/**
 * The moves that are just a move — no decision, nobody's words on the record.
 * These are the ones allowed onto the row, in this order.
 */
const FORWARD: readonly SubmittalStatus[] = ['internal_review', 'submitted', 'draft'];

const MIN_REASON = 20;

interface Props {
  readonly projectId: string;
  /**
   * One submittal, as its own page, from
   * `/projects/<id>/design/submittals/<recordId>`.
   *
   * A submittal EARNS a page. Its approval record is a permanent list nobody can
   * edit afterwards, what was sent is a file trail, and what it is holding up is
   * a third list — and that third one is the sentence `06` calls "the condition
   * that matters": four days overdue AND three materials that cannot be ordered.
   * Stacked in a 480px drawer, the reader scrolls past exactly the part that
   * makes the delay mean something.
   */
  readonly recordId?: string | null;
  readonly can: {
    readonly write: boolean;
    readonly edit: boolean;
    readonly remove: boolean;
    /** `submittal: X`. Gates the button; the rows are already filtered. */
    readonly exportRows: boolean;
  };
}

/**
 * What the drawer is doing. `null` means it is shut.
 *
 * `detail` is gone: the submittal has a page and an address now (§4.3). The
 * three FORMS stay, decisions included — a decision goes on a permanent record
 * and demands the consultant's words, which is a form and not a record.
 */
type DrawerMode =
  | { readonly kind: 'edit'; readonly id: string }
  | { readonly kind: 'decide'; readonly id: string; readonly to: SubmittalStatus }
  | { readonly kind: 'add' };

export function Submittals({ projectId, recordId = null, can }: Props): JSX.Element {
  const [items, setItems] = useState<readonly Submittal[] | null>(null);
  /** How many exist on the server, vs how many are loaded — see `api/paging.ts`. */
  const [registerTotal, setRegisterTotal] = useState(0);
  const [wantAll, setWantAll] = useState(false);
  const [drawings, setDrawings] = useState<readonly Deliverable[]>([]);
  /**
   * The materials waiting on these submittals — RULE P1 read backwards.
   *
   * A submittal's real cost is what it is holding up, and until now that was
   * visible only from the other register. Allowed to fail silently, like the
   * drawings picker above: a consultant who may decide a submittal and may not
   * read the material register loses this line, not the screen.
   */
  const [materials, setMaterials] = useState<readonly Material[]>([]);
  /** Not the same as an empty register — see `Drawings.tsx`. */
  const [denied, setDenied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [drawer, setDrawer] = useState<DrawerMode | null>(null);
  /** Set by the form inside the drawer, so closing it can ask before discarding. */
  const [formDirty, setFormDirty] = useState(false);
  const view = useRegisterView('due');

  /**
   * The drawings the picker offers.
   *
   * ITS OWN FUNCTION BECAUSE IT IS RE-READ WHEN THE FORM OPENS, and that is the
   * fix for a stale dropdown that showed a snapshot of the register from
   * whenever this screen was first drawn.
   *
   * Drawings and submittals share the Design tab, so both registers are on
   * screen at once. Issue a revision on the drawing above, then raise a
   * submittal below, and the picker was still answering from before: a drawing
   * you had just issued still read "issue a revision first", and one you had
   * just added was not in the list at all. The register two inches up the page
   * said otherwise, which is the worst possible pairing.
   *
   * Allowed to fail on its own: a caller who may raise a submittal but not read
   * the drawings register should lose the dropdown, not the screen.
   */
  const loadDrawings = useCallback(async () => {
    try {
      // A supporting list, not this screen's register: only the rows.
      setDrawings((await listDeliverables(projectId)).rows);
    } catch {
      setDrawings([]);
    }
  }, [projectId]);

  const load = useCallback(async () => {
    try {
      const page = await listSubmittals(projectId, wantAll ? LOAD_ALL_LIMIT : undefined);
      setItems(page.rows);
      setRegisterTotal(page.total);
      setDenied(false);
    } catch (caught) {
      const refused = caught instanceof ApiError && (caught.status === 403 || caught.status === 404);
      setDenied(refused);
      setError(refused ? null : 'Could not load the submittals.');
      setItems([]);
    }
    await loadDrawings();
    try {
      // A supporting list, not this screen's register: it only needs the rows.
      setMaterials((await listMaterials(projectId)).rows);
    } catch {
      setMaterials([]);
    }
  }, [projectId, wantAll, loadDrawings]);

  useEffect(() => {
    void load();
  }, [load]);

  /*
   * RE-READ THE DRAWINGS THE MOMENT THE FORM OPENS.
   *
   * The picker is the only thing on this screen that describes ANOTHER
   * register, and that register is on the same tab — so it can change under it
   * without this component hearing about it. Opening the form is exactly when
   * its answer has to be current, and it is also cheap: nobody who never raises
   * a submittal pays for it.
   *
   * `decide` is left out on purpose. That drawer records a consultant's answer
   * and shows no picker, so refreshing for it would be a request for nothing.
   */
  const formOpen = drawer?.kind === 'add' || drawer?.kind === 'edit';
  useEffect(() => {
    if (!formOpen) return;
    void loadDrawings();
  }, [formOpen, loadDrawings]);

  async function run(work: () => Promise<readonly Submittal[]>): Promise<boolean> {
    setBusy(true);
    setError(null);
    try {
      setItems(await work());
      return true;
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That did not work.');
      return false;
    } finally {
      setBusy(false);
    }
  }

  /** Shut the drawer, and forget whether its form had edits in it. */
  const closeDrawer = useCallback(() => {
    setDrawer(null);
    setFormDirty(false);
    setError(null);
  }, []);

  /**
   * The rows on screen.
   *
   * Filtering lives HERE rather than in `useRegisterView`, so every dependency
   * is visible to `react-hooks/exhaustive-deps` — which this repo treats as an
   * error. It runs before the early returns below because hooks must.
   */
  const shown = useMemo(() => {
    if (!items) return [];

    const stage = view.chosen.stage;

    const filtered = items.filter((s) => {
      // Everything somebody would type: the number, what it is, who is holding
      // it, and the drawing it came off.
      const hay = [s.number, s.title, s.recipient_name, s.deliverable_number]
        .filter(Boolean)
        .join(' ');
      if (!matches(hay, view.query)) return false;
      if (stage === 'awaiting' && (s.sla === 'not_sent' || s.sla === 'closed')) return false;
      if (stage === 'late' && s.sla !== 'overdue' && s.sla !== 'red') return false;
      if (stage === 'answered' && s.sla !== 'closed') return false;
      if (stage === 'not_sent' && s.sla !== 'not_sent') return false;
      return true;
    });

    // A null date sorts LAST, never first — a submittal with no response date
    // has not gone out, and it is not the most urgent thing on the register.
    const byDue = (a: Submittal, b: Submittal): number => {
      const x = a.required_response_date;
      const y = b.required_response_date;
      if (x === y) return 0;
      if (x === null) return 1;
      if (y === null) return -1;
      return x < y ? -1 : 1;
    };

    const compare: Readonly<Record<string, (a: Submittal, b: Submittal) => number>> = {
      due: byDue,
      // Worst first. Anything not overdue has no lateness to compare and sorts
      // below everything that has.
      overdue: (a, b) => (b.days_overdue ?? -1) - (a.days_overdue ?? -1),
      // A submittal round three has been round the loop twice, which is the
      // thing a Design Coordinator is looking for when they ask.
      round: (a, b) => b.round - a.round || a.number.localeCompare(b.number),
      number: (a, b) => a.number.localeCompare(b.number),
    };

    return [...filtered].sort(compare[view.sortKey] ?? compare.due!);
  }, [items, view.query, view.chosen.stage, view.sortKey]);

  /** The record the drawer is about. `null` for the add form, which has none. */
  const subject =
    drawer && drawer.kind !== 'add' ? (items?.find((s) => s.id === drawer.id) ?? null) : null;

  /** Whether this move needs words on the record before it can happen. */
  function needsWords(to: SubmittalStatus): boolean {
    return DECISIONS.includes(to) || to === 'void';
  }

  /** Make a move, opening the decision form first where one is required. */
  function move(submittal: Submittal, to: SubmittalStatus): void {
    setError(null);
    if (needsWords(to)) {
      setDrawer({ kind: 'decide', id: submittal.id, to });
      return;
    }
    void run(() => moveSubmittal(projectId, submittal.id, { status: to }));
  }

  /**
   * The one move that goes on the row.
   *
   * Only ever a move, never a decision — see the note at the top of the file.
   * A submittal whose only remaining moves are decisions gets "Open".
   */
  function primaryFor(submittal: Submittal): RowAction {
    const forward = can.edit
      ? FORWARD.find((status) => submittal.allowed_next.includes(status))
      : undefined;
    if (forward) {
      return {
        label: MOVE_LABELS[forward] ?? forward,
        disabled: busy,
        onSelect: () => move(submittal, forward),
      };
    }
    return {
      label: 'Open',
      onSelect: () => navigate(recordHref(projectId, 'submittals', submittal.id)),
    };
  }

  /**
   * Everything that is not the one visible action.
   *
   * The same list serves the row and the record page. Two entries differ, and
   * both for the same reason: on the page you are already looking at the record.
   */
  function overflowFor(
    submittal: Submittal,
    primary: RowAction | null,
    onPage = false,
  ): readonly RowAction[] {
    const actions: RowAction[] = onPage
      ? []
      : [
          {
            label: 'Open',
            onSelect: () => navigate(recordHref(projectId, 'submittals', submittal.id)),
          },
        ];
    // Only while it is in-house. Once it is out, the dates it was sent under
    // are part of the record and there is no legitimate way to move them.
    if (can.edit && (submittal.status === 'draft' || submittal.status === 'internal_review')) {
      actions.push({
        label: 'Edit',
        onSelect: () => setDrawer({ kind: 'edit', id: submittal.id }),
      });
    }
    // The register never offers a move the machine has not returned, so a
    // button here cannot be one the service then refuses.
    for (const to of submittal.allowed_next) {
      actions.push({
        label: MOVE_LABELS[to] ?? to,
        danger: to === 'void' || to === 'rejected',
        disabled: busy,
        onSelect: () => move(submittal, to),
      });
    }
    if (can.remove) {
      actions.push({
        label: 'Delete',
        danger: true,
        disabled: busy,
        onSelect: () => {
          void run(() => deleteSubmittal(projectId, submittal.id)).then((ok) => {
            // Deleting the record you are standing on leaves you on an address
            // that names nothing. The register is where the deletion makes
            // sense, so that is where it puts you.
            if (ok && onPage) navigate(sectionHref(projectId, 'design'));
          });
        },
      });
    }
    // Whatever is already on the row must not also be in the menu behind it.
    return primary ? actions.filter((action) => action.label !== primary.label) : actions;
  }

  async function exportRows(): Promise<void> {
    const headers = [
      'Submittal',
      'Title',
      'Status',
      'With',
      'Response due',
      'Standing',
      'Days overdue',
      'Round',
      'Drawing',
      'Void reason',
    ];
    const rows = shown.map((s) => [
      s.number,
      s.title,
      STATUS_LABELS[s.status],
      s.recipient_name ?? '',
      s.required_response_date ?? '',
      SLA_WORDS[s.sla],
      s.days_overdue !== null && s.days_overdue > 0 ? String(s.days_overdue) : '',
      String(s.round),
      s.deliverable_number
        ? `${s.deliverable_number}${s.revision_code ? ` Rev ${s.revision_code}` : ''}`
        : '',
      s.void_reason ?? '',
    ]);
    setError(null);
    try {
      // Recorded BEFORE the file is written, and refusable — `09` §Threats
      // wants the row count and the filters. See `register-export.ts`.
      await exportRegister({
        projectId,
        resource: 'submittal',
        filename: `submittals-${projectId.slice(0, 8)}.csv`,
        headers,
        rows,
        filters: { query: view.query, ...view.chosen },
      });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That did not export.');
    }
  }

  const head = (
    <div className="section-head sub-head-row">
      <h3 className="sub-head">Submittals</h3>
      {items && can.write && (
        <button type="button" className="inline" onClick={() => setDrawer({ kind: 'add' })}>
          Raise submittal
        </button>
      )}
    </div>
  );

  /*
   * The same four states on a record page, and still four different sentences.
   * The furniture above them changes: "Raise submittal" is the wrong offer to
   * somebody who followed a link to one particular submittal.
   */
  const above = recordId ? <RecordBack projectId={projectId} register="submittals" /> : head;

  if (denied) {
    return (
      <>
        {above}
        <p className="hint">Your role does not include this project&rsquo;s submittals.</p>
      </>
    );
  }

  if (items === null) {
    return (
      <>
        {above}
        <p aria-live="polite">Loading {recordId ? 'the submittal' : 'the submittals'}…</p>
      </>
    );
  }

  const out = items.filter((s) => s.sla !== 'not_sent' && s.sla !== 'closed');
  const late = out.filter((s) => s.sla === 'overdue' || s.sla === 'red');

  /**
   * The three forms, shared by the register and the record page.
   *
   * The same drawers on both, and the decision form above all: the words typed
   * into it go onto a permanent record that nobody can edit or delete
   * afterwards. A page with its own copy of that form is a second place for that
   * rule to drift.
   */
  const drawers = (
    <>
      {drawer?.kind === 'add' && (
        <Drawer title="Raise a submittal" dirty={formDirty} onClose={closeDrawer}>
          <SubmittalForm
            drawings={drawings}
            busy={busy}
            onDirtyChange={setFormDirty}
            onCancel={closeDrawer}
            onSubmit={async (input) => {
              if (await run(() => createSubmittal(projectId, input))) closeDrawer();
            }}
          />
        </Drawer>
      )}

      {drawer?.kind === 'edit' && subject && (
        <Drawer
          title={`Edit ${subject.number}`}
          subtitle={subject.title}
          dirty={formDirty}
          onClose={closeDrawer}
        >
          <SubmittalForm
            existing={subject}
            drawings={drawings}
            busy={busy}
            onDirtyChange={setFormDirty}
            onCancel={closeDrawer}
            onSubmit={async (input) => {
              if (await run(() => updateSubmittal(projectId, subject.id, input))) closeDrawer();
            }}
          />
        </Drawer>
      )}

      {drawer?.kind === 'decide' && subject && (
        <Drawer
          title={`${MOVE_LABELS[drawer.to] ?? drawer.to} — ${subject.number}`}
          subtitle={subject.title}
          dirty={formDirty}
          onClose={closeDrawer}
        >
          <DecisionForm
            to={drawer.to}
            busy={busy}
            onDirtyChange={setFormDirty}
            onCancel={closeDrawer}
            onSubmit={async (input) => {
              const to = drawer.to;
              if (await run(() => moveSubmittal(projectId, subject.id, { status: to, ...input }))) {
                closeDrawer();
              }
            }}
          />
        </Drawer>
      )}
    </>
  );

  const alert = error && (
    <div className="alert" role="alert">
      <span>{error}</span>
    </div>
  );

  // ----------------------------------------------------- one submittal, in full
  if (recordId) {
    const record = items.find((s) => s.id === recordId) ?? null;
    if (!record) {
      /* A FAILED LOAD IS NOT A MISSING RECORD — see `Materials.tsx`. */
      return error ? (
        <>
          {above}
          {alert}
        </>
      ) : (
        <RecordMissing projectId={projectId} register="submittals" />
      );
    }

    /*
     * `primaryFor` answers "Open" when the only moves left are DECISIONS — the
     * deliberate bend in the register pattern described at the top of this file.
     * On the record page that is a button that navigates to the page you are
     * already on, so it is dropped rather than shown as a no-op, and the
     * decisions stay where the bend puts them: behind More, one deliberate
     * click away, never arguing for an answer.
     */
    const forward = primaryFor(record);
    const primary = forward.label === 'Open' ? null : forward;
    /* NO BAR WHEN THERE IS NOTHING IN IT — see `Drawings.tsx`. */
    const more = overflowFor(record, primary, true);
    const actions =
      primary || more.length > 0 ? (
        <RowActions rowLabel={record.number} {...(primary ? { primary } : {})} more={more} />
      ) : null;
    const holding = blockedBy(materials, record.id);

    return (
      <>
        {alert}
        <RecordPage
          projectId={projectId}
          register="submittals"
          reference={record.number}
          title={record.title}
          status={STATUS_LABELS[record.status]}
          meta={
            <>
              <span className="sla-word">{SLA_WORDS[record.sla]}</span>
              {record.days_overdue !== null && record.days_overdue > 0 && (
                <span className="task-overdue">
                  {' '}
                  by {record.days_overdue} {record.days_overdue === 1 ? 'day' : 'days'}
                </span>
              )}
              {/* Round stays visible here for the same reason it stays on the
                  row at both densities: a submittal going round for the third
                  time is the fact that changes what somebody does about it. */}
              {record.round > 1 && <span className="task-overdue"> · round {record.round}</span>}
            </>
          }
          {...(actions ? { actions } : {})}
          properties={<SubmittalProperties submittal={record} projectId={projectId} />}
        >
          {/*
            WHAT IT IS HOLDING UP COMES FIRST, above the paperwork and above the
            decisions. RULE P1 read backwards: an approval releases the materials
            waiting on it, so this is the line that turns "four days overdue" into
            "four days overdue and three materials cannot be ordered" — which `06`
            calls the condition that matters. On the drawer it was the third thing
            down and got scrolled past.
          */}
          <div className="register record-section">
            <div className="section-head sub-head-row">
              <h4 className="sub-head">What it is holding up</h4>
            </div>
            {holding.length > 0 ? (
              <p className="hint">
                <BlockedMaterials projectId={projectId} materials={holding} />
              </p>
            ) : (
              /*
               * Silence here is ambiguous — nothing linked, or a reader who may
               * not read the material register at all (the fetch is allowed to
               * fail quietly for exactly that consultant). It says which.
               */
              <p className="hint">
                No material on this project is recorded as waiting on this submittal. A material
                is linked to the submittal that releases it from the material register.
              </p>
            )}
          </div>

          <div className="register record-section">
            <div className="section-head sub-head-row">
              <h4 className="sub-head">What was sent</h4>
            </div>
            {/*
              Shared by default: a consultant asked to approve something they
              cannot open has been asked to approve nothing.
            */}
            <Attachments
              projectId={projectId}
              entityType="submittal"
              entityId={record.id}
              can={{ write: can.edit }}
              noun="what was sent"
              category="drawing"
              shareByDefault
            />
          </div>

          <div className="register record-section">
            <div className="section-head sub-head-row">
              <h4 className="sub-head">The approval record</h4>
            </div>
            {record.history.length > 0 ? (
              <History submittal={record} />
            ) : (
              <p className="hint">
                Nothing has been decided yet. Every decision made on this submittal is kept here
                permanently, and nobody can edit or delete it afterwards.
              </p>
            )}
          </div>
        </RecordPage>
        {drawers}
      </>
    );
  }

  return (
    <>
      {head}

      {alert}

      {items.length > 0 && (
        <p className="hint" role="status">
          <strong>{out.length} awaiting a response</strong>
          {late.length > 0 && (
            <>
              {' · '}
              <span className="task-overdue">
                {late.length} past {late.length === 1 ? 'its' : 'their'} response date
              </span>
            </>
          )}
          . The Approvals stage cannot close while anything is past its date.
        </p>
      )}

      {items.length === 0 ? (
        <div className="empty">
          <p>
            <strong>Nothing has been sent for approval.</strong>
          </p>
          <p>
            {can.write
              ? 'Raise one against an issued drawing and give it a response date. The date is the deadline the consultant is held to, and it is what the whole register is measured against.'
              : /*
                 * An empty state must name an action ITS READER can take. A
                 * Client or Consultant holds `submittal: VA` — they decide one,
                 * they never raise one — so the sentence for them says who does.
                 */
                'A submittal is a drawing sent out for a decision, with a deadline attached. The Design Coordinator or the Project Manager raises them; only the client or consultant can decide one, which is what makes the answer worth having.'}
          </p>
        </div>
      ) : (
        <>
          <RegisterToolbar
            view={view}
            searchLabel="Search submittals"
            searchPlaceholder="Number, title, who has it"
            shown={shown.length}
            total={items.length}
            registerTotal={registerTotal}
            onLoadAll={() => setWantAll(true)}
            loadingAll={wantAll && items.length < registerTotal}
            noun={['submittal', 'submittals']}
            filters={[
              {
                key: 'stage',
                label: 'Show',
                options: [
                  { value: 'awaiting', label: 'Awaiting a response' },
                  { value: 'late', label: 'Past its response date' },
                  { value: 'answered', label: 'Answered' },
                  { value: 'not_sent', label: 'Not sent yet' },
                ],
              },
            ]}
            sorts={[
              { key: 'due', label: 'Response due' },
              { key: 'overdue', label: 'Most overdue' },
              { key: 'round', label: 'Most rounds' },
              { key: 'number', label: 'Reference' },
            ]}
          >
            {can.exportRows && (
              <button type="button" className="secondary inline" onClick={() => void exportRows()}>
                Export {shown.length === items.length ? 'all' : 'these'} to CSV
              </button>
            )}
          </RegisterToolbar>

          {shown.length === 0 ? (
            /*
             * A DIFFERENT empty state from "nothing has been sent". This reader
             * has a full register and a filter that excludes all of it; telling
             * them to raise one would be advice about the wrong problem.
             */
            <div className="empty">
              <p>
                <strong>No submittal matches that.</strong>
              </p>
              <p>
                <button type="button" className="link-button" onClick={view.clear}>
                  Clear the search and filters
                </button>{' '}
                to see all {items.length}.
              </p>
            </div>
          ) : (
            <div className={`rk-scroll${view.density === 'compact' ? ' rk-compact' : ''}`}>
              <table className="grid rk-cards">
                <caption className="visually-hidden">
                  Submittals, with who has them, the response date and how overdue each one is.{' '}
                  {shown.length} of {items.length} shown.
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Submittal</th>
                    <th scope="col">With</th>
                    <th scope="col">Response due</th>
                    <th scope="col">Standing</th>
                    <th scope="col">
                      <span className="visually-hidden">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((s) => {
                    const primary = primaryFor(s);
                    return (
                      <SubmittalRow key={s.id} submittal={s}>
                        <td>
                          <span className="ms-symbol" aria-hidden="true">
                            {STATUS_SYMBOLS[s.status]}
                          </span>{' '}
                          {/*
                            A REAL LINK, because the record has an address now —
                            so a middle-click, a new tab and a copied link all
                            mean what they look like they mean.
                          */}
                          <AppLink
                            className="rk-open"
                            to={recordHref(projectId, 'submittals', s.id)}
                          >
                            <strong>{s.number}</strong> {s.title}
                            <span className="visually-hidden"> — open this submittal</span>
                          </AppLink>
                          <span className="tree-code"> {STATUS_LABELS[s.status]}</span>
                          {/*
                            Round stays at BOTH densities, like lateness: a
                            submittal going round for the third time is the fact
                            that changes what somebody does about it.
                          */}
                          {s.round > 1 && <span className="task-overdue"> · round {s.round}</span>}
                          {/*
                            SECONDARY. Hidden at compact density, and all of it
                            is on the record's drawer as well, so nothing is
                            visible at only one density.
                          */}
                          <span className="rk-secondary">
                            {s.deliverable_number && (
                              <span className="tree-code">
                                {' '}
                                <CrossLink
                                  projectId={projectId}
                                  section="design"
                                  register="drawings"
                                  focus={s.deliverable_id}
                                  says="the drawing this submittal was raised against"
                                >
                                  {s.deliverable_number}
                                  {s.revision_code ? ` Rev ${s.revision_code}` : ''}
                                </CrossLink>
                              </span>
                            )}
                            <BlockedMaterials
                              projectId={projectId}
                              materials={blockedBy(materials, s.id)}
                            />
                            {s.void_reason && (
                              <span className="tree-code"> Void: {s.void_reason}</span>
                            )}
                          </span>
                        </td>
                        <td data-label="With">{s.recipient_name ?? '—'}</td>
                        <td data-label="Response due">{formatDate(s.required_response_date)}</td>
                        <td data-label="Standing">
                          <span className="sla-word">{SLA_WORDS[s.sla]}</span>
                          {s.days_overdue !== null && s.days_overdue > 0 && (
                            <span className="task-overdue">
                              {' '}
                              by {s.days_overdue} {s.days_overdue === 1 ? 'day' : 'days'}
                            </span>
                          )}
                        </td>
                        <td>
                          <RowActions
                            rowLabel={s.number}
                            primary={primary}
                            more={overflowFor(s, primary)}
                          />
                        </td>
                      </SubmittalRow>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {drawers}
    </>
  );
}

// ---------------------------------------------------------------------------

/**
 * What the submittal IS — the record page's rail.
 *
 * Everything compact density hides is here. It was the drawer's whole body until
 * the submittal earned a page; the three LISTS — what it is holding up, what was
 * sent, and the approval record — moved to the main column. The approval record
 * in particular used to open as a panel under the whole table, so the submittal
 * being read and its own record were never on screen together.
 */
function SubmittalProperties({
  submittal,
  projectId,
}: {
  readonly submittal: Submittal;
  readonly projectId: string;
}): JSX.Element {
  return (
    <>
      <dl className="detail-grid">
        <dt>Standing</dt>
        <dd>
          <span className="sla-word">{SLA_WORDS[submittal.sla]}</span>
          {submittal.days_overdue !== null && submittal.days_overdue > 0 && (
            <span className="task-overdue">
              {' '}
              by {submittal.days_overdue} {submittal.days_overdue === 1 ? 'day' : 'days'}
            </span>
          )}
        </dd>
        <dt>With</dt>
        <dd>{submittal.recipient_name ?? '—'}</dd>
        <dt>Response due</dt>
        <dd>{formatDate(submittal.required_response_date)}</dd>
        <dt>Round</dt>
        <dd>{submittal.round}</dd>
        {submittal.deliverable_number && (
          <>
            <dt>Raised against</dt>
            <dd>
              <CrossLink
                projectId={projectId}
                section="design"
                register="drawings"
                focus={submittal.deliverable_id}
                says="the drawing this submittal was raised against"
              >
                {submittal.deliverable_number}
                {submittal.revision_code ? ` Rev ${submittal.revision_code}` : ''}
              </CrossLink>
            </dd>
          </>
        )}
        {submittal.void_reason && (
          <>
            <dt>Voided because</dt>
            <dd>{submittal.void_reason}</dd>
          </>
        )}
      </dl>
    </>
  );
}

// ---------------------------------------------------------------------------

/** The materials this submittal is holding up. */
function blockedBy(materials: readonly Material[], submittalId: string): readonly Material[] {
  return materials.filter((m) => m.submittal_id === submittalId);
}

/**
 * What this submittal is holding up, on the submittal's own row.
 *
 * RULE P1 says an approval RELEASES the materials waiting on it, in the same
 * transaction. This is that rule read backwards, and it is the sentence that
 * turns "four days overdue" into "four days overdue and three materials cannot
 * be ordered" — `06` calls that second one "the condition that matters".
 */
function BlockedMaterials({
  projectId,
  materials,
}: {
  readonly projectId: string;
  readonly materials: readonly Material[];
}): JSX.Element | null {
  if (materials.length === 0) return null;
  const blocked = materials.filter((m) => m.status === 'blocked_pending_approval');

  return (
    <span className="mat-exception">
      {blocked.length > 0 ? (
        <>
          Holding up {blocked.length} {blocked.length === 1 ? 'material' : 'materials'}:{' '}
        </>
      ) : (
        <>Linked to: </>
      )}
      {materials.map((m, index) => (
        <span key={m.id}>
          {index > 0 && ', '}
          <CrossLink
            projectId={projectId}
            section="procurement"
            register="materials"
            focus={m.id}
            says={
              m.status === 'blocked_pending_approval'
                ? 'a material blocked until this is approved'
                : 'a material linked to this submittal'
            }
          >
            {m.number}
          </CrossLink>
        </span>
      ))}
    </span>
  );
}

/** One row, which knows whether a cross-link came looking for it. */
function SubmittalRow({
  submittal,
  children,
}: {
  readonly submittal: Submittal;
  readonly children: React.ReactNode;
}): JSX.Element {
  const { focused, ref } = useFocusRow<HTMLTableRowElement>(submittal.id);
  return (
    <tr
      ref={ref}
      className={focusClass(focused, `sub-${submittal.status} sla-${submittal.sla}`)}
      aria-current={focused ? 'true' : undefined}
    >
      {children}
    </tr>
  );
}

// ---------------------------------------------------------------------------

const ACTION_WORDS: Readonly<Record<string, string>> = {
  submitted: 'Sent for approval',
  approved: 'Approved',
  approved_with_comments: 'Approved with comments',
  rejected: 'Rejected',
  resubmit_required: 'Resubmission requested',
  void: 'Voided',
};

/**
 * The approval record.
 *
 * Every decision ever made on this submittal, including the ones from earlier
 * rounds that the current status has forgotten. Nothing here can be edited or
 * deleted, by anybody, including a Company Admin — the grant itself is revoked.
 */
function History({ submittal }: { readonly submittal: Submittal }): JSX.Element {
  return (
    <div className="panel">
      {/*
        NO HEADING OF ITS OWN. The record page's section is already headed "The
        approval record", and the same rule that took `panel-title` off every
        form when the drawer landed applies here: two headings for one list read
        as a mistake.
      */}
      <ol className="sub-history">
        {submittal.history.map((a) => (
          <li key={a.id}>
            <strong>{ACTION_WORDS[a.action] ?? a.action}</strong>
            <span className="diary-late">
              {' '}
              {formatDate(a.occurred_at)} by {a.actor_name ?? 'someone'}
              {a.revision_code ? ` · Rev ${a.revision_code}` : ''}
            </span>
            {a.comment && <span className="diary-summary">{a.comment}</span>}
          </li>
        ))}
      </ol>
      <p className="hint">
        This record cannot be edited or deleted by anyone, including an administrator. It is what
        the approval is worth in a dispute.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------

interface FormProps {
  /** Absent when raising a new one. */
  readonly existing?: Submittal;
  readonly drawings: readonly Deliverable[];
  readonly busy: boolean;
  readonly onCancel: () => void;
  /** Lets the drawer ask before throwing away what has been typed. */
  readonly onDirtyChange?: (dirty: boolean) => void;
  readonly onSubmit: (input: {
    title: string;
    deliverable_id: string | null;
    revision_id: string | null;
    recipient_name: string | null;
    required_response_date: string | null;
  }) => void | Promise<void>;
}

function SubmittalForm({
  existing,
  drawings,
  busy,
  onCancel,
  onDirtyChange,
  onSubmit,
}: FormProps): JSX.Element {
  const [title, setTitle] = useState(existing?.title ?? '');
  const [drawingId, setDrawingId] = useState(existing?.deliverable_id ?? '');
  const [recipient, setRecipient] = useState(existing?.recipient_name ?? '');
  const [date, setDate] = useState(existing?.required_response_date ?? '');

  /*
   * Compared against the record rather than tracked with a touched flag:
   * somebody who types a character and deletes it has changed nothing and
   * should not be asked to confirm.
   */
  useEffect(() => {
    onDirtyChange?.(
      title !== (existing?.title ?? '') ||
        drawingId !== (existing?.deliverable_id ?? '') ||
        recipient !== (existing?.recipient_name ?? '') ||
        date !== (existing?.required_response_date ?? ''),
    );
  }, [title, drawingId, recipient, date, existing, onDirtyChange]);

  /*
   * Only issued drawings can go out for approval — sending a revision that does
   * not exist yet is a promise, not a submittal.
   *
   * THE ONES THAT CANNOT GO ARE STILL LISTED, greyed out, saying why.
   *
   * They used to be filtered away completely, and that is what the first person
   * to test this screen hit within minutes: add a drawing, come here to send it,
   * and it is simply not in the list. Nothing accounted for it. The hint below
   * explained the rule only when the list was ENTIRELY empty — so on a project
   * with other drawings already issued, the one you just made vanished in
   * silence.
   *
   * A disabled option is the right shape, and it does not contradict the rule
   * that a control somebody may not use should be ABSENT rather than
   * present-and-refusing. That rule is about ACTIONS. This is a list somebody
   * is scanning for a name they know is there, and the useful answer to "where
   * is my drawing?" is to show it with the reason attached.
   */
  const issuable = drawings.filter((d) => d.revisions.length > 0);
  const notYetIssuable = drawings.filter((d) => d.revisions.length === 0);
  const chosen = issuable.find((d) => d.id === drawingId);
  const currentRevision = chosen?.revisions.find((r) => r.is_current) ?? null;

  function submit(event: FormEvent): void {
    event.preventDefault();
    if (!title.trim()) return;
    void onSubmit({
      title: title.trim(),
      deliverable_id: drawingId || null,
      revision_id: currentRevision?.id ?? null,
      recipient_name: recipient.trim() || null,
      required_response_date: date || null,
    });
  }

  return (
    <form className="panel" onSubmit={submit}>
      {existing && <p className="panel-title">{existing.number}</p>}
      <div className="field-row">
        <div className="field">
          <label htmlFor="sub-title">What is being submitted?</label>
          <input
            id="sub-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
            disabled={busy}
            placeholder="Reception ceiling plan for approval"
          />
        </div>
        <div className="field">
          <label htmlFor="sub-drawing">Against which drawing?</label>
          <select
            id="sub-drawing"
            value={drawingId}
            disabled={busy}
            onChange={(e) => setDrawingId(e.target.value)}
          >
            <option value="">Not linked to a drawing</option>
            {issuable.map((d) => (
              <option key={d.id} value={d.id}>
                {d.number} {d.title}
                {d.current_revision ? ` (Rev ${d.current_revision})` : ''}
              </option>
            ))}
            {/*
              Present, and unpickable, and saying why — see the note above
              `notYetIssuable`. The suffix is on the OPTION rather than in a
              footnote because a long list is scrolled, not read, and the reason
              has to arrive at the moment the eye lands on the name.
            */}
            {notYetIssuable.map((d) => (
              <option key={d.id} value={d.id} disabled>
                {d.number} {d.title} — issue a revision first
              </option>
            ))}
          </select>
          <p className="hint">
            {issuable.length === 0 && notYetIssuable.length === 0
              ? 'There are no drawings on this project yet.'
              : issuable.length === 0
                ? 'No drawing has a revision issued yet, so there is nothing to send. Open the drawing, press Start, then Issue revision.'
                : notYetIssuable.length > 0
                  ? `The current revision goes out, and which one was sent is part of the record. ${notYetIssuable.length} drawing${notYetIssuable.length === 1 ? ' is' : 's are'} greyed out above because nothing has been issued against ${notYetIssuable.length === 1 ? 'it' : 'them'} yet.`
                  : 'The current revision goes out. Which one was sent is part of the record.'}
          </p>
        </div>
      </div>

      <div className="field-row">
        <div className="field">
          <label htmlFor="sub-recipient">Who is deciding?</label>
          <input
            id="sub-recipient"
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
            maxLength={200}
            disabled={busy}
            placeholder="Omar Haddad, lead consultant"
          />
        </div>
        <div className="field">
          <label htmlFor="sub-date">Response needed by</label>
          <input
            id="sub-date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            disabled={busy}
          />
          <p className="hint">
            Required before it can be sent, and fixed once it is. It is the deadline the project
            is measured against.
          </p>
        </div>
      </div>

      <div className="button-row">
        <button type="submit" className="inline" disabled={busy || !title.trim()}>
          {busy ? 'Saving…' : existing ? 'Save' : 'Raise it'}
        </button>
        <button type="button" className="secondary inline" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------

/** No `submittal`: the drawer's heading already says which one is being decided. */
interface DecisionProps {
  readonly to: SubmittalStatus;
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onDirtyChange?: (dirty: boolean) => void;
  readonly onSubmit: (input: { comment?: string; reason?: string }) => void | Promise<void>;
}

function DecisionForm({
  to,
  busy,
  onCancel,
  onDirtyChange,
  onSubmit,
}: DecisionProps): JSX.Element {
  const [text, setText] = useState('');
  const isVoid = to === 'void';
  const enough = isVoid ? text.trim().length >= MIN_REASON : true;

  useEffect(() => {
    onDirtyChange?.(text !== '');
  }, [text, onDirtyChange]);

  return (
    <div className="panel">
      <p className="hint">
        {isVoid
          ? 'A voided submittal keeps its number forever and it is never reused, so the record has to say why.'
          : 'This goes on the approval record permanently. Nobody can edit or delete it afterwards, including an administrator.'}
      </p>

      <div className="field">
        <label htmlFor="sub-comment">{isVoid ? 'Why is it being voided?' : 'Comments'}</label>
        <textarea
          id="sub-comment"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={3}
          maxLength={4000}
          disabled={busy}
          placeholder={
            isVoid
              ? 'Raised against the wrong drawing; superseded by SUB-0009.'
              : 'Ceiling heights clash with the MEP layout on Level 3.'
          }
        />
        {isVoid && (
          <p className="hint">
            {text.trim().length}/{MIN_REASON} characters minimum.
          </p>
        )}
      </div>

      <div className="button-row">
        <button
          type="button"
          className="inline"
          disabled={busy || !enough}
          onClick={() =>
            // Built conditionally: with exactOptionalPropertyTypes, an absent
            // comment and `comment: undefined` are different shapes, and the
            // wire wants absence.
            void onSubmit(
              isVoid ? { reason: text.trim() } : text.trim() ? { comment: text.trim() } : {},
            )
          }
        >
          {busy ? 'Saving…' : MOVE_LABELS[to]}
        </button>
        <button type="button" className="secondary inline" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </div>
  );
}
